<template>
  <div
    ref="panelGroup"
    class="cf-node-properties__panel-group row"
  >
    <GenericProps
      :node-id="node.id"
      :settings.sync="settings"
      :audio-parameters="audioParameters"
      :parameters="filteredNodeParameters"
      @update:settings="updateNodeParent"
    />

    <div class="cf-node-properties__field col-md-12">
      <legend
        v-t="'MENU_ROUTES'"
        class="cf-node-properties__legend"
      />

      <Draggable
        v-model="routeList"
      >
        <RoutePanel
          v-for="route in routeList"
          :key="route.id"
          :index="route.index"
          :route.sync="route"
          :add-routes="true"
          :sortable="true"
          @addNodeToRoute="addNodeToRoute($event, route)"
        />
      </Draggable>
    </div>
  </div>
</template>

<script>
import nodePropsMixin from '@/modules/ivrs/mixins/nodePropsMixin';
import { cloneDeep, merge } from 'lodash';
import Draggable from 'vuedraggable';
import { mapState } from 'vuex';
import {
  createSettingsHierarchy,
} from '../../../../helpers/node';
import messages from '../../../../locales';
import RoutePanel from '../Form/RoutePanel';

const ivrMenuParameterNames = [ 'timeOutInSeconds', 'retries', 'menuPrompt', 'menuTagName', 'voiceResponse' ];
const legacyMenuParameterNames = [ 'timeOutInSeconds', 'retries', 'menuPrompt', 'menuTagName' ];

export default {
  name: 'MenuNodeProps',

  components: {
    RoutePanel,
    Draggable,
  },

  mixins: [nodePropsMixin],

  props: {
    routes: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      menuRoutes: [],
      audioParameters: Object.freeze(['menuPrompt'])
    };
  },

  computed: {

     ...mapState({
      featureFlags: 'currentAccountFeatureFlags'
    }),
    
    ivrMenuFeatureFlag() {
      return this.featureFlags.ivr_menu;
    },

    filteredNodeParameters() {
      return this.buildFilteredParameters();
    },

    routeList: {
      get() {
        return this.menuRoutes;
      },
      set(newList) {
        let i = 0;
        newList = newList.map((route) => {
          i = (((i + 1) % 10) + 10) % 10;

          return {
            ...route,
            name: 'Press ' + i,
            index: i,
          };
        });

        this.$set(this, 'menuRoutes', newList);
        this.emitNewRoutes();
      },
    },
  },
  watch: {
    routes: {
      handler() {
        this.setMenuRoutes();
      },
      immediate: true
    },
  },

  created() {
    this.buildNodeSettings();
  },

  methods: {
    buildFilteredParameters() {
      // if ivr menu flag is set use new parameters
      if (this.ivrMenuFeatureFlag) {
        return this.node.parameters.filter((parameter) => ivrMenuParameterNames.includes(parameter.name)); 
      } else { // if ivr menu flag is not set, use legacy parameters
        return this.node.parameters.filter((parameter) => legacyMenuParameterNames.includes(parameter.name));
      }
    },
    setMenuRoutes() {
      this.menuRoutes = [];
      const availableNumbers = Array.from(
        { length: 10 },
        (_, i) => (((i + 1) % 10) + 10) % 10
      );
      for (const availableNumber of availableNumbers) {
        const existingRoute = this.routes.find((route) => {
          return route.filter == availableNumber;
        });
        if (existingRoute) {
          this.menuRoutes.push({
            index: availableNumber,
            name: 'Press ' + availableNumber,
            ...existingRoute,
          });
        } else {
          this.menuRoutes.push({
            index: availableNumber,
            name: 'Press ' + availableNumber,
            displayName: 'Empty',
            criteria: [],
          });
        }
      }
    },
    emitNewRoutes() {
      let routes = cloneDeep(this.menuRoutes).filter(route => route.targetNode != null);

      routes = routes.map((route) => {
        return {
          ...route,
          filter: route.index,
          criteria: [
            {
              tagCriteria: [
                {
                  comparisonType: 'EQUALS',
                  isNegativeMatch: false,
                  isNumber: false,
                  tagIds: ['menu:' + this.node.id],
                  value: route.index,
                }
              ]
            }
          ]
        };
      });
      this.$emit('update:routes', routes);
    },
    
    buildNodeSettings() {
      // Sets value of settings data
      this.settings = createSettingsHierarchy(this.node, undefined, undefined, this.node.settings);
      // Merges built settings hierarchy with current node settings
      this.settings = merge(this.settings.root, this.node.settings);
      
      this.handleMessage(this.settings.menuPrompt);

      this.updateNodeParent();
    },
    
    addNodeToRoute(event, route) {
      if (route.targetNode) {
        this.$emit('removeConnection', route.targetNode.id);
      }

      this.$emit('showContextMenu', event, {
        node: this.node,
        slotNumber: 0,
        isOnFailure: false,
        filter: route.index,
      });
    },
    
    // Prepares payload for saving the route configuration
    // Not currently used, but if we continue to use the editLink endpoint, the updated route will need to use this to prepare the payload
    updateLocalRouteSettings() {},

    handleMessage(settings) {
      if (settings.play.URL) {
        settings.isEnabled = true;
        settings.play.isEnabled = true;
      }
      if (settings.say.text) {
        settings.isEnabled = true;
        settings.say.isEnabled = true;
      }
    },
  },
  i18n: {
    messages,
    locale: 'en',
  },
};
</script>